import React, { useState, useRef, useCallback, useEffect } from 'react';
import { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';
import FileUploadStyled from 'components/ContractorAdvancedFileUpload/FileUpload.styled';
import { IconFileText } from "theme/common/Icon.styled"

interface FileUploadProps {
  name: string;
  label: string;
  icon?: any;
  required?: boolean;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  watch: any;
  acceptedTypes?: string;
  maxSize?: number;
  onConfirm: () => void;
}

export const ContractorAdvancedFileUpload: React.FC<FileUploadProps> = ({
  name,
  label,
  icon: Icon = IconFileText,
  required = false,
  register,
  errors,
  watch,
  onConfirm,
  acceptedTypes = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB por defecto
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const lastProcessedFileRef = useRef<string>('');
  const isMountedRef = useRef(true);

  // Memoizar el callback para evitar recreaciones innecesarias
  const memoizedOnConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  // Cleanup effect para prevenir actualizaciones de estado en componentes desmontados
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Función helper para actualizar el estado de manera segura
  const safeSetIsValidating = useCallback((value: boolean) => {
    if (isMountedRef.current) {
      setIsValidating(value);
    }
  }, []);

  const validateFile = async (files: FileList | undefined) => {
    safeSetIsValidating(true);
    
    // Simular un pequeño delay para mostrar el loading
    await new Promise(resolve => setTimeout(resolve, 250));
    
    try {
      if (required && (!files || files.length === 0)) {
        safeSetIsValidating(false);
        return "El archivo es obligatorio";
      }
      
      if (files && files[0]) {
        const file = files[0];
        
        // Crear un identificador único para el archivo basado en nombre, tamaño y última modificación
        const fileIdentifier = `${file.name}-${file.size}-${file.lastModified}`;
        
        // Verificar si este archivo ya fue procesado
        if (lastProcessedFileRef.current === fileIdentifier) {
          safeSetIsValidating(false);
          return true; // Retornar true sin ejecutar onConfirm nuevamente
        }
        
        if (file.size > maxSize) {
          safeSetIsValidating(false);
          return `El archivo no debe superar ${maxSize / (1024 * 1024)}MB`;
        }
        // Validar tipo de archivo usando patrones MIME
        const isValidType = acceptedTypes.split(',').some(type => {
          const trimmedType = type.trim();
          if (trimmedType.includes('*')) {
            // Para patrones como "image/*", verificar que el tipo base coincida
            const baseType = trimmedType.split('/')[0];
            return file.type.startsWith(baseType + '/');
          } else {
            // Para tipos específicos como "image/jpeg", verificar coincidencia exacta
            return file.type === trimmedType;
          }
        });
        
        if (!isValidType) {
          safeSetIsValidating(false);
          return "Formato de archivo no permitido";
        }
        
        // Marcar este archivo como procesado
        lastProcessedFileRef.current = fileIdentifier;
        
        safeSetIsValidating(false);
        memoizedOnConfirm(); // Ejecutar onConfirm solo una vez por archivo único
        
        return true;
      }
      
      safeSetIsValidating(false);
      return true;
    } catch (error) {
      safeSetIsValidating(false);
      return "Error al validar el archivo";
    }
  };

  return (
    <FileUploadStyled>
      <div className={`loading-container ${isValidating ? 'loading-disabled' : ''}`}>
        <label className="block">
          <div className="box-file">
            <Icon className="icon" />
            <input
              type="file"
              className="hidden"
              accept={acceptedTypes}
              disabled={isValidating}
              {...register(name, { validate: validateFile })}
            />
            <button
              type="button"
              disabled={isValidating}
              onClick={() => document.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.click()}
            >
              Seleccionar archivo
            </button>
          </div>
        </label>
        {isValidating && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p className="loading-text">Validando archivo...</p>
          </div>
        )}
        {errors[name] && (
          <p className="text-error">{errors[name]?.message as string}</p>
        )}
      </div>
    </FileUploadStyled>
  );
};